import React, { useMemo } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { legendLabelsReflectLineDash } from './chartJsLegendLineDash';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TransitServicePoint {
  year: number;
  miles: number;
}

interface TransitServiceExpansionChartsProps {
  location: string;
  historicalServiceMiles: TransitServicePoint[];
  percentIncrease: number;
  baselineServiceMiles?: number;
  baselineYear?: number;
  implementationYear?: number;
  projectionEndYear?: number;
}

const milesFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const buildYearRange = (startYear: number, endYear: number): number[] => {
  if (endYear < startYear) return [];
  return Array.from({ length: endYear - startYear + 1 }, (_, idx) => startYear + idx);
};

const estimateAnnualTrendMiles = (
  history: TransitServicePoint[],
  baselineMiles: number
): number => {
  if (history.length < 2) {
    return Math.max(0, baselineMiles * 0.005); // fallback 0.5% annual growth
  }

  // Use recent observations to reflect current trend.
  const recent = history.slice(-6);
  const yearToYearChanges: number[] = [];

  for (let idx = 1; idx < recent.length; idx += 1) {
    const prev = recent[idx - 1];
    const curr = recent[idx];
    const yearDelta = curr.year - prev.year;
    if (yearDelta <= 0) continue;
    yearToYearChanges.push((curr.miles - prev.miles) / yearDelta);
  }

  if (!yearToYearChanges.length) {
    return Math.max(0, baselineMiles * 0.005);
  }

  const avgAnnualDelta =
    yearToYearChanges.reduce((total, delta) => total + delta, 0) / yearToYearChanges.length;

  // Keep projections gently increasing even when the recent average is flat/noisy.
  return Math.max(avgAnnualDelta, baselineMiles * 0.0025);
};

const TransitServiceExpansionCharts: React.FC<TransitServiceExpansionChartsProps> = ({
  location,
  historicalServiceMiles,
  percentIncrease,
  baselineServiceMiles,
  baselineYear,
  implementationYear = 2025,
  projectionEndYear = 2045,
}) => {
  const cleanedHistorical = useMemo(
    () =>
      [...historicalServiceMiles]
        .filter(
          (point) =>
            Number.isFinite(point.year) &&
            Number.isFinite(point.miles) &&
            point.year >= 1990 &&
            point.year <= 2100 &&
            point.miles > 0
        )
        .map((point) => ({ year: point.year, miles: point.miles }))
        .sort((a, b) => a.year - b.year),
    [historicalServiceMiles]
  );

  const latestHistoricalPoint = cleanedHistorical.length
    ? cleanedHistorical[cleanedHistorical.length - 1]
    : null;
  const effectiveBaselineMiles = latestHistoricalPoint?.miles ?? Math.max(0, baselineServiceMiles ?? 0);
  const effectiveBaselineYear = latestHistoricalPoint?.year ?? baselineYear ?? implementationYear - 1;
  const annualTrendMiles = estimateAnnualTrendMiles(cleanedHistorical, effectiveBaselineMiles);
  const annualTrendPercent =
    effectiveBaselineMiles > 0 ? (annualTrendMiles / effectiveBaselineMiles) * 100 : 0;

  const historicalSeed = cleanedHistorical.length
    ? cleanedHistorical
    : effectiveBaselineMiles > 0
    ? [{ year: effectiveBaselineYear, miles: effectiveBaselineMiles }]
    : [];

  const projectionStartYear = effectiveBaselineYear + 1;
  const projectionYears = buildYearRange(projectionStartYear, projectionEndYear);

  const projectedNoPolicy = projectionYears.map((year) => {
    const yearsFromBaseline = year - effectiveBaselineYear;
    return Math.max(0, effectiveBaselineMiles + annualTrendMiles * yearsFromBaseline);
  });
  const projectedWithPolicy = projectionYears.map((year, idx) => {
    const noPolicyValue = projectedNoPolicy[idx] || 0;
    if (year < implementationYear) return noPolicyValue;
    return noPolicyValue * (1 + percentIncrease / 100);
  });

  const scenarioLabels = [
    ...historicalSeed.map((point) => String(point.year)),
    ...projectionYears.map((year) => String(year)),
  ];
  const noPolicySeries = [
    ...historicalSeed.map((point) => point.miles),
    ...projectedNoPolicy,
  ];
  const withPolicySeries = [
    ...historicalSeed.map((point) => point.miles),
    ...projectedWithPolicy,
  ];
  const annualAddedServiceSeries = projectionYears.map((year, idx) =>
    year < implementationYear ? 0 : Math.max(0, projectedWithPolicy[idx] - projectedNoPolicy[idx])
  );

  const lineOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: false,
          boxWidth: 28,
          generateLabels: legendLabelsReflectLineDash,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${milesFormatter.format(Number(context.parsed.y || 0))} miles`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => milesFormatter.format(Number(value)),
        },
      },
    },
  };

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${milesFormatter.format(Number(context.parsed.y || 0))} miles`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => milesFormatter.format(Number(value)),
        },
      },
    },
  };

  const hasAnyTransitServiceData = historicalSeed.length > 0 || effectiveBaselineMiles > 0;

  if (!hasAnyTransitServiceData) {
    return (
      <div className="transit-service-charts-section">
        <h4 className="transit-service-charts-title">Transit Service Over Time</h4>
        <p className="transit-service-charts-empty">
          Historical transit service miles are not available yet for {location}.
        </p>
      </div>
    );
  }

  return (
    <div className="transit-service-charts-section">
      <h4 className="transit-service-charts-title">Transit Service Over Time</h4>
      <p className="transit-service-charts-subtitle">
        Baseline transit service is approximately {milesFormatter.format(effectiveBaselineMiles)} miles in{' '}
        {effectiveBaselineYear}. Future projections continue the recent trend at about{' '}
        {annualTrendPercent.toFixed(2)}% per year; the policy case adds {percentIncrease}% above that trend
        starting in {implementationYear}.
      </p>

      <div className="transit-service-charts-grid">
        <div className="transit-service-chart-card">
          <h5>Historical transit service miles</h5>
          <div className="transit-service-chart-container">
            <Line
              data={{
                labels: historicalSeed.map((point) => String(point.year)),
                datasets: [
                  {
                    label: 'Observed transit service miles',
                    data: historicalSeed.map((point) => point.miles),
                    borderColor: '#2563eb',
                    backgroundColor: 'rgba(37, 99, 235, 0.2)',
                    tension: 0.25,
                    fill: true,
                    pointRadius: 2,
                  },
                ],
              }}
              options={lineOptions}
            />
          </div>
        </div>

        <div className="transit-service-chart-card">
          <h5>Historical + projected service (trend + policy)</h5>
          <div className="transit-service-chart-container">
            <Line
              data={{
                labels: scenarioLabels,
                datasets: [
                  {
                    label: 'No policy (continues recent trend)',
                    data: noPolicySeries,
                    borderColor: '#6b7280',
                    backgroundColor: 'rgba(107, 114, 128, 0.15)',
                    tension: 0.2,
                    fill: false,
                    pointRadius: 1.5,
                    borderDash: [5, 4],
                  },
                  {
                    label: `With policy (+${percentIncrease}% above trend)`,
                    data: withPolicySeries,
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.18)',
                    tension: 0.2,
                    fill: false,
                    pointRadius: 1.5,
                  },
                ],
              }}
              options={lineOptions}
            />
          </div>
        </div>

        <div className="transit-service-chart-card">
          <h5>Annual added transit service from policy</h5>
          <div className="transit-service-chart-container">
            <Bar
              data={{
                labels: projectionYears.map((year) => String(year)),
                datasets: [
                  {
                    label: 'Added service miles per year',
                    data: annualAddedServiceSeries,
                    backgroundColor: 'rgba(14, 165, 233, 0.65)',
                    borderColor: '#0284c7',
                    borderWidth: 1,
                  },
                ],
              }}
              options={barOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransitServiceExpansionCharts;
